import type { ComponentType, SVGProps } from 'react'
import { cn } from '@/lib/cn'

/** Un'icona Heroicons (o qualunque componente SVG con la stessa firma). */
export type HeroIcon = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>

const sizeMap = {
  xs: 'w-3.5 h-3.5',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-7 h-7',
  // Dimensioni "da emoji grande": icone-eroe di stati vuoti, onboarding, card.
  '2xl': 'w-8 h-8',
  '3xl': 'w-10 h-10',
  '4xl': 'w-12 h-12',
  '5xl': 'w-16 h-16',
} as const

export interface IconProps {
  icon: HeroIcon
  /** Se presente, l'icona è semantica (role="img" + aria-label); altrimenti è decorativa (aria-hidden). */
  label?: string
  size?: keyof typeof sizeMap
  className?: string
  /** Icona in linea con il testo (sostituisce un'emoji usata come prefisso): allineata alla linea di base. */
  inline?: boolean
}

/**
 * Wrapper unico per le icone: standardizza dimensione e accessibilità.
 * Decorativa di default (aria-hidden, esclusa dalla Tab order dello screen reader);
 * passare `label` quando l'icona veicola informazione da sola.
 */
export default function Icon({ icon: Glyph, label, size = 'md', className, inline }: IconProps) {
  const a11y = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': true, focusable: false } as const)
  return <Glyph className={cn(sizeMap[size], inline && 'inline-block align-[-0.125em]', className)} {...a11y} />
}
